import { ref, watch, onBeforeUnmount } from 'vue';
import API from 'dashboard/api/bugReports';
import { useBugReportUpload } from './useBugReportUpload';

export function useBugReports(accountId) {
  const { upload, uploading, uploadError, cancelUpload } = useBugReportUpload();
  const reports = ref([]);
  const selected = ref(null);
  const total = ref(0);
  const busy = ref(false);
  const error = ref(false);
  const unavailable = ref(false);
  let generation = 0;
  let pending = 0;
  let selectionGeneration = 0;

  const clearSelection = () => {
    selectionGeneration += 1;
    selected.value = null;
  };

  const reset = () => {
    cancelUpload();
    generation += 1;
    pending = 0;
    reports.value = [];
    clearSelection();
    total.value = 0;
    busy.value = false;
    error.value = false;
    unavailable.value = false;
  };
  watch(accountId, reset, { flush: 'sync' });
  onBeforeUnmount(reset);

  const request = async (operation, apply, isCurrent = () => true) => {
    const current = generation;
    const account = accountId.value;
    pending += 1;
    busy.value = true;
    error.value = false;
    try {
      const { data } = await operation(account);
      if (current !== generation || !isCurrent()) return false;
      unavailable.value = false;
      apply(data);
      return true;
    } catch (e) {
      if (current !== generation) return false;
      if ([401, 403, 404].includes(e.response?.status)) {
        reset();
        unavailable.value = true;
      } else if (isCurrent()) {
        error.value = true;
      }
      return false;
    } finally {
      if (current === generation) {
        pending -= 1;
        busy.value = pending > 0;
      }
    }
  };
  const list = page =>
    request(
      id => API.list(id, page),
      data => {
        reports.value = data.reports;
        total.value = data.total;
      }
    );
  const selectRequest = operation => {
    selectionGeneration += 1;
    const selection = selectionGeneration;
    return request(
      operation,
      data => {
        selected.value = data;
      },
      () => selection === selectionGeneration
    );
  };
  const show = reportId => selectRequest(id => API.show(id, reportId));
  const create = (attributes, files) =>
    selectRequest(id =>
      upload(options => API.create(id, attributes, files, options))
    );
  const comment = (body, files, submissionId) =>
    selectRequest(id =>
      upload(options =>
        API.comment(id, selected.value.id, body, files, submissionId, options)
      )
    );
  const download = image =>
    request(
      id => API.download(id, selected.value.id, image.id),
      data => {
        const objectUrl = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = image.filename;
        link.click();
        URL.revokeObjectURL(objectUrl);
      }
    );
  return {
    uploading,
    uploadError,
    cancelUpload,
    reports,
    selected,
    total,
    busy,
    error,
    unavailable,
    list,
    show,
    create,
    comment,
    download,
    clearSelection,
  };
}
